import { DropZoneContext } from './Models/DropZoneContext';

/**
 * Represents a drop handler for handling drag and drop events.
 */
export class DropHandler {
    private elementSettings: Map<HTMLElement, DropZoneContext> = new Map();
    private dragEnterHandler: any;
    private onDragOverHandler: any;
    private onDropHandler: any;
    private dragLeaveHandler: any;
    private dragOverTarget: HTMLElement;
    private dragOverDropzone: HTMLElement;

    /**
     * Initializes the drop handler for the specified element with the given context.
     * 
     * @param element The HTML element to initialize the drop handler for.
     * @param context The drop zone context.
     */
    constructor() {
        this.dragEnterHandler = this.onDragEnter.bind(this);
        this.onDragOverHandler = this.onDragOver.bind(this);
        this.onDropHandler = this.onDrop.bind(this);
        this.dragLeaveHandler = this.onDragLeave.bind(this);
    }

    /**
     * Disposes the specified HTML element.
     * 
     * @param element The HTML element to dispose.
     */
    public dispose(element: HTMLElement) {
        this.removeEventListeners(element);
    }

    /**
     * Sets the specified HTML element with the given context.
     * 
     * @param element The HTML element to set.
     * @param context The drop zone context.
     */
    public setElement(element: HTMLElement, context: DropZoneContext) {
        this.elementSettings.set(element, context);

        if (context.isDisabled) {
            this.removeEventListeners(element);
        }
        else {
            this.addEventListeners(element);
        }
    }

    /**
     * Handles the drag enter event.
     * 
     * @param event The drag event.
     */
    private onDragEnter(event: DragEvent): void {
        event.stopPropagation();

        const element = event.currentTarget as HTMLElement;
        if (this.dragOverDropzone !== element && this.dragOverDropzone) {
            this.clearActiveElement(this.dragOverDropzone);
        }

        this.dragOverTarget = event.target as HTMLElement;
        this.dragOverDropzone = element;

        if (this.isElementAllowed(element) === false) {
            return;
        }

        let context = this.elementSettings.get(element);
        this.setActiveElement(element);
        window['DragHandler'].onDropTargetEnter(element, context.settings);
    }

    /**
     * Adds the event listeners
     * @param element 
     * @returns 
     */
    private addEventListeners(element: HTMLElement) {
        if (element === null) {
            return;
        }

        element.addEventListener('dragenter', this.dragEnterHandler);
        element.addEventListener('dragover', this.onDragOverHandler);
        element.addEventListener('drop', this.onDropHandler);
        element.addEventListener('dragleave', this.dragLeaveHandler);
    }

    /**
     * Removes the event listeners
     * @param element 
     * @returns 
     */
    private removeEventListeners(element: HTMLElement) {
        if (element === null) {
            return;
        }

        element.removeEventListener('dragenter', this.dragEnterHandler);
        element.removeEventListener('dragover', this.onDragOverHandler);
        element.removeEventListener('drop', this.onDropHandler);
        element.removeEventListener('dragleave', this.dragLeaveHandler);
    }
    
    /**
     * Handles the drag over event.
     * 
     * @param event The drag event.
     */
    private onDragOver(event: DragEvent): void {
        const element = event.currentTarget as HTMLElement;
        if (this.isElementAllowed(element) === false) {
            return;
        }
        event.preventDefault();
    }

    /**
     * Handles the drop event.
     * 
     * @param event The drag event.
     */
    private onDrop(event: DragEvent): void {
        event.stopPropagation();

        const element = event.currentTarget as HTMLElement;
        let context = this.elementSettings.get(element);
        const droppedItemContext = window['DragHandler'].getElementContext();

        this.clearActiveElement(element);
        context.dotNetRef.invokeMethodAsync('ItemDropped', droppedItemContext);
    }

    /**
     * Handles the drag leave event.
     * 
     * @param event The drag event.
     */
    private onDragLeave(event: DragEvent): void {
        event.stopPropagation();

        if (event.target !== this.dragOverTarget) {
            return;
        }

        const element = event.currentTarget as HTMLElement;
        this.clearActiveElement(element);
        window['DragHandler'].onDropTargetLeave(element);
    }

    /**
     * Checks if the specified HTML element is allowed.
     * 
     * @param element The HTML element to check.
     * @returns True if the element is allowed, false otherwise.
     */
    private isElementAllowed(element: HTMLElement): boolean {
        const context = this.elementSettings.get(element);

        if (context.isDisabled) {
            return false;
        }

        return context.allowedType === null || context.allowedType.trim() === '' || context.allowedType.split(' ').indexOf(window['DragHandler'].getElementType()) !== -1;
    }

    /**
     * Sets the specified HTML element as the active target.
     * 
     * @param element The HTML element to set as active.
     */
    private setActiveElement(element: HTMLElement) {
        element.classList.add("active-target");

        const settings = this.elementSettings.get(element);
        if (settings.dragOverClasses !== null) {
            element.classList.add(...settings.dragOverClasses.split(' '));
        }

        if (settings.dragOverStyle !== null) {
            element.style.cssText = settings.dragOverStyle;
        }
    }

    /**
     * Clears the active element.
     * 
     * @param element The HTML element to clear.
     */
    private clearActiveElement(element: HTMLElement) {
        element.classList.remove("active-target");

        const settings = this.elementSettings.get(element);
        if (settings.dragOverClasses !== null) {
            element.classList.remove(...settings.dragOverClasses.split(' '));
        }

        if (settings.dragOverStyle !== null) {
            // remove styles from the element based on the styles set in the settings
            const styles = settings.dragOverStyle.split(';');
            for (let style of styles) {
                const styleParts = style.split(':');
                if (styleParts.length === 2) {
                    element.style.removeProperty(styleParts[0].trim());
                }
            }
        }
    }
}

window["DropHandler"] = new DropHandler();